import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Breadcrumb, Card, Spinner, Alert, Tabs, Tab } from "react-bootstrap";
import { Link } from "react-router-dom";
import { HomeOutlined, BarChartOutlined } from "@ant-design/icons";
import { Statistic, Select, Tag } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line, Pie, Doughnut, Radar } from "react-chartjs-2";
import {
  fetchDepartmentPerformance,
  fetchTaskRejections,
  fetchTaskTrends,
  fetchTaskComplexity,
  fetchWorkloadDistribution,
  fetchApprovalChainAnalysis,
  fetchTimeAnalysis,
} from "@/store/analyticsApi/analyticsThunk";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { Option } = Select;

const AnalyticsPage = () => {
  const dispatch = useDispatch();
  const [periodDays, setPeriodDays] = useState(30);
  const [trendPeriod, setTrendPeriod] = useState("daily");
  const [activeTab, setActiveTab] = useState("departments");

  const {
    departmentPerformance,
    taskRejections,
    taskTrends,
    taskComplexity,
    workloadDistribution,
    approvalChainAnalysis,
    timeAnalysis,
    departmentPerformanceLoading,
    taskRejectionsLoading,
    taskTrendsLoading,
    taskComplexityLoading,
    workloadDistributionLoading,
    approvalChainAnalysisLoading,
    timeAnalysisLoading,
    departmentPerformanceError,
    taskRejectionsError,
    taskTrendsError,
  } = useSelector((state) => state.analytics);

  useEffect(() => {
    dispatch(fetchDepartmentPerformance({ periodDays }));
    dispatch(fetchTaskRejections({ periodDays }));
    dispatch(fetchTaskComplexity({ periodDays }));
    dispatch(fetchWorkloadDistribution());
    dispatch(fetchApprovalChainAnalysis());
    dispatch(fetchTimeAnalysis({ periodDays }));
  }, [dispatch, periodDays]);

  useEffect(() => {
    dispatch(fetchTaskTrends({ period: trendPeriod, days: periodDays }));
  }, [dispatch, trendPeriod, periodDays]);

  // Department Performance Chart Data
  const departmentChartData = departmentPerformance?.departments?.length > 0 ? {
    labels: departmentPerformance.departments.map(d => d.department_name),
    datasets: [
      {
        label: "Bajarilgan topshiriqlar",
        data: departmentPerformance.departments.map(d => d.tasks_completed),
        backgroundColor: "rgba(76, 175, 80, 0.6)",
        borderColor: "rgba(76, 175, 80, 1)",
        borderWidth: 1,
      },
      {
        label: "Biriktirilgan topshiriqlar",
        data: departmentPerformance.departments.map(d => d.tasks_assigned),
        backgroundColor: "rgba(33, 150, 243, 0.6)",
        borderColor: "rgba(33, 150, 243, 1)",
        borderWidth: 1,
      },
    ],
  } : null;

  // Task Rejections Chart Data
  const rejectionReasonsChartData = taskRejections?.rejection_reasons?.length > 0 ? {
    labels: taskRejections.rejection_reasons.map(r => r.reason_category),
    datasets: [{
      data: taskRejections.rejection_reasons.map(r => r.count),
      backgroundColor: [
        "rgba(244, 67, 54, 0.8)",
        "rgba(255, 152, 0, 0.8)",
        "rgba(255, 193, 7, 0.8)",
        "rgba(156, 39, 176, 0.8)",
        "rgba(63, 81, 181, 0.8)",
      ],
    }],
  } : null;

  // Task Trends Chart Data
  const taskTrendsChartData = taskTrends?.data_points?.length > 0 ? {
    labels: taskTrends.data_points.map(d => d.date),
    datasets: [
      {
        label: "Yaratilgan",
        data: taskTrends.data_points.map(d => d.tasks_created),
        borderColor: "rgba(33, 150, 243, 1)",
        backgroundColor: "rgba(33, 150, 243, 0.1)",
        fill: true,
      },
      {
        label: "Bajarilgan",
        data: taskTrends.data_points.map(d => d.tasks_completed),
        borderColor: "rgba(76, 175, 80, 1)",
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        fill: true,
      },
      {
        label: "Tasdiqlangan",
        data: taskTrends.data_points.map(d => d.tasks_approved),
        borderColor: "rgba(156, 39, 176, 1)",
        backgroundColor: "rgba(156, 39, 176, 0.1)",
        fill: true,
      },
    ],
  } : null;

  // Task Complexity Chart Data
  const taskComplexityChartData = taskComplexity?.complexity_distribution?.length > 0 ? {
    labels: taskComplexity.complexity_distribution.map(c => c.complexity),
    datasets: [{
      data: taskComplexity.complexity_distribution.map(c => c.count),
      backgroundColor: [
        "rgba(76, 175, 80, 0.8)",
        "rgba(255, 193, 7, 0.8)",
        "rgba(255, 152, 0, 0.8)",
        "rgba(244, 67, 54, 0.8)",
      ],
    }],
  } : null;

  // Workload Distribution Chart Data
  const workloadByStatusChartData = workloadDistribution?.by_status ? {
    labels: ["Light", "Normal", "Heavy", "Overloaded"],
    datasets: [{
      data: [
        workloadDistribution.by_status.light || 0,
        workloadDistribution.by_status.normal || 0,
        workloadDistribution.by_status.heavy || 0,
        workloadDistribution.by_status.overloaded || 0,
      ],
      backgroundColor: [
        "rgba(76, 175, 80, 0.8)",
        "rgba(33, 150, 243, 0.8)",
        "rgba(255, 152, 0, 0.8)",
        "rgba(244, 67, 54, 0.8)",
      ],
    }],
  } : null;

  // Time Analysis Chart Data
  const timeAnalysisHourlyChartData = timeAnalysis?.hourly_distribution?.length > 0 ? {
    labels: timeAnalysis.hourly_distribution.map(h => `${h.hour}:00`),
    datasets: [{
      label: "Faoliyat",
      data: timeAnalysis.hourly_distribution.map(h => h.activity_count),
      backgroundColor: "rgba(33, 150, 243, 0.6)",
      borderColor: "rgba(33, 150, 243, 1)",
      borderWidth: 1,
    }],
  } : null;
  return (
    <Container fluid className="p-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Analitika</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        {/*<h2>*/}
        {/*  <BarChartOutlined className="me-2" />*/}
        {/*  Analitika va Hisobotlar*/}
        {/*</h2>*/}
        <Select
          value={periodDays}
          onChange={setPeriodDays}
          style={{ width: 200 }}
        >
          <Option value={7}>So'nggi 7 kun</Option>
          <Option value={30}>So'nggi 30 kun</Option>
          <Option value={90}>So'nggi 90 kun</Option>
          <Option value={365}>So'nggi yil</Option>
        </Select>
      </div>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        {/* Department Performance Tab */}
        <Tab eventKey="departments" title="Bo'limlar">
          <Card>
            <Card.Body>
              <h5 className="mb-3">Bo'limlar bo'yicha ishlash ko'rsatkichlari</h5>
              {departmentPerformanceLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : departmentPerformanceError ? (
                <Alert variant="danger">{departmentPerformanceError}</Alert>
              ) : departmentChartData ? (
                <>
                  <div style={{ height: "400px" }} className="mb-4">
                    <Bar
                      data={departmentChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: "top",
                          },
                        },
                      }}
                    />
                  </div>
                  <Row>
                    {departmentPerformance.departments.map((dept) => (
                      <Col key={dept.department_id} xs={12} md={6} lg={4} className="mb-3">
                        <Card>
                          <Card.Body>
                            <h6>{dept.department_name}</h6>
                            <Statistic
                              title="Bajarilish foizi"
                              value={Math.round(dept.completion_rate)}
                              suffix="%"
                              valueStyle={{
                                color: dept.completion_rate >= 80 ? "#52c41a" : dept.completion_rate >= 50 ? "#faad14" : "#ff4d4f"
                              }}
                            />
                            <p className="mb-0 mt-2 text-muted small">
                              {dept.tasks_completed} / {dept.tasks_assigned} bajarildi
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </>
              ) : (
                <p className="text-muted">Ma'lumot mavjud emas</p>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Task Rejections Tab */}
        <Tab eventKey="rejections" title="Rad etilganlar">
          <Row className="g-3">
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="mb-3">Rad etish sabablari</h5>
                  {taskRejectionsLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : rejectionReasonsChartData ? (
                    <div style={{ height: "300px" }}>
                      <Doughnut
                        data={rejectionReasonsChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "bottom",
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted">Ma'lumot mavjud emas</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="mb-3">Eng ko'p rad etilgan topshiriqlar</h5>
                  {taskRejectionsLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : taskRejections?.most_rejected_tasks?.length > 0 ? (
                    <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                      {taskRejections.most_rejected_tasks.map((task, idx) => (
                        <div key={task.task_id} className="mb-3 pb-3 border-bottom">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1">#{idx + 1} {task.task_title}</h6>
                              <p className="text-muted mb-0 small">Yaratgan: {task.creator_name}</p>
                            </div>
                            <Tag color="red">{task.rejection_count} marta</Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">Ma'lumot mavjud emas</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Task Trends Tab */}
        <Tab eventKey="trends" title="Trendlar">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Topshiriqlar trendi</h5>
                <Select value={trendPeriod} onChange={setTrendPeriod} style={{ width: 150 }}>
                  <Option value="daily">Kunlik</Option>
                  <Option value="weekly">Haftalik</Option>
                  <Option value="monthly">Oylik</Option>
                </Select>
              </div>
              {taskTrendsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : taskTrendsError ? (
                <Alert variant="danger">{taskTrendsError}</Alert>
              ) : taskTrendsChartData ? (
                <div style={{ height: "400px" }}>
                  <Line
                    data={taskTrendsChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "top",
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <p className="text-muted">Ma'lumot mavjud emas</p>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Task Complexity Tab */}
        <Tab eventKey="complexity" title="Murakkablik">
          <Row className="g-3">
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="mb-3">Topshiriqlar murakkabligi</h5>
                  {taskComplexityLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : taskComplexityChartData ? (
                    <div style={{ height: "300px" }}>
                      <Pie
                        data={taskComplexityChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "bottom",
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted">Ma'lumot mavjud emas</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="mb-3">Murakkablik statistikasi</h5>
                  {taskComplexity?.complexity_distribution?.map((item) => (
                    <div key={item.complexity} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-capitalize">{item.complexity}</span>
                        <span className="fw-bold">{item.count} ta</span>
                      </div>
                      <div className="progress">
                        <div
                          className="progress-bar"
                          style={{ width: `${item.percentage}%` }}
                          role="progressbar"
                        >
                          {Math.round(item.percentage)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Workload Distribution Tab */}
        {/*<Tab eventKey="workload" title="Ish yuki">*/}
        {/*  <Row className="g-3">*/}
        {/*    <Col xs={12} lg={6}>*/}
        {/*      <Card className="h-100">*/}
        {/*        <Card.Body>*/}
        {/*          <h5 className="mb-3">Ish yuki taqsimoti</h5>*/}
        {/*          {workloadDistributionLoading ? (*/}
        {/*            <div className="text-center py-5">*/}
        {/*              <Spinner animation="border" />*/}
        {/*            </div>*/}
        {/*          ) : workloadByStatusChartData ? (*/}
        {/*            <div style={{ height: "300px" }}>*/}
        {/*              <Doughnut*/}
        {/*                data={workloadByStatusChartData}*/}
        {/*                options={{*/}
        {/*                  responsive: true,*/}
        {/*                  maintainAspectRatio: false,*/}
        {/*                  plugins: {*/}
        {/*                    legend: {*/}
        {/*                      position: "bottom",*/}
        {/*                    },*/}
        {/*                  },*/}
        {/*                }}*/}
        {/*              />*/}
        {/*            </div>*/}
        {/*          ) : (*/}
        {/*            <p className="text-muted">Ma'lumot mavjud emas</p>*/}
        {/*          )}*/}
        {/*        </Card.Body>*/}
        {/*      </Card>*/}
        {/*    </Col>*/}
        {/*    <Col xs={12} lg={6}>*/}
        {/*      <Card className="h-100">*/}
        {/*        <Card.Body>*/}
        {/*          <h5 className="mb-3">Eng ko'p vazifa yuklangan xodimlar</h5>*/}
        {/*          {workloadDistributionLoading ? (*/}
        {/*            <div className="text-center py-5">*/}
        {/*              <Spinner animation="border" />*/}
        {/*            </div>*/}
        {/*          ) : workloadDistribution?.top_loaded_users?.length > 0 ? (*/}
        {/*            <div style={{ maxHeight: "350px", overflowY: "auto" }}>*/}
        {/*              {workloadDistribution.top_loaded_users.slice(0, 10).map((user, idx) => (*/}
        {/*                <div key={user.user_id} className="mb-3 pb-3 border-bottom">*/}
        {/*                  <div className="d-flex justify-content-between align-items-center">*/}
        {/*                    <div>*/}
        {/*                      <h6 className="mb-1">#{idx + 1} {user.name}</h6>*/}
        {/*                      <small className="text-muted">{user.active_tasks} faol topshiriq</small>*/}
        {/*                    </div>*/}
        {/*                    <Tag color={*/}
        {/*                      user.workload_status === "overloaded" ? "red" :*/}
        {/*                      user.workload_status === "heavy" ? "orange" :*/}
        {/*                      user.workload_status === "normal" ? "blue" : "green"*/}
        {/*                    }>*/}
        {/*                      {user.workload_status}*/}
        {/*                    </Tag>*/}
        {/*                  </div>*/}
        {/*                </div>*/}
        {/*              ))}*/}
        {/*            </div>*/}
        {/*          ) : (*/}
        {/*            <p className="text-muted">Ma'lumot mavjud emas</p>*/}
        {/*          )}*/}
        {/*        </Card.Body>*/}
        {/*      </Card>*/}
        {/*    </Col>*/}
        {/*  </Row>*/}
        {/*</Tab>*/}

        {/* Time Analysis Tab */}
        <Tab eventKey="time" title="Vaqt tahlili">
          <Card>
            <Card.Body>
              <h5 className="mb-3">Soatlik faoliyat taqsimoti</h5>
              {timeAnalysisLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : timeAnalysisHourlyChartData ? (
                <div style={{ height: "400px" }}>
                  <Bar
                    data={timeAnalysisHourlyChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <p className="text-muted">Ma'lumot mavjud emas</p>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Approval Chain Tab */}
        <Tab eventKey="approval" title="Tasdiqlash zanjiri">
          <Row className="g-3">
            <Col xs={12} sm={6} lg={3}>
              <Card>
                <Card.Body>
                  <Statistic
                    title="O'rtacha tasdiqlash vaqti"
                    value={approvalChainAnalysis?.avg_approval_time_hours?.toFixed(1) || "N/A"}
                    suffix="soat"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <Card>
                <Card.Body>
                  <Statistic
                    title="Tasdiqlash zanjiri uzunligi"
                    value={approvalChainAnalysis?.avg_chain_length?.toFixed(1) || "N/A"}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <Card>
                <Card.Body>
                  <Statistic
                    title="Birinchi tasdiqda"
                    value={Math.round(approvalChainAnalysis?.first_time_approval_rate || 0)}
                    suffix="%"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <Card>
                <Card.Body>
                  <Statistic
                    title="Qaytarilgan topshiriqlar"
                    value={Math.round(approvalChainAnalysis?.rejection_rate || 0)}
                    suffix="%"
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default AnalyticsPage;
